'use client';

import { c as _c } from "react/compiler-runtime";
import { Bot, ChevronsDownUp, ChevronsUpDown, Copy, ExternalLink, FileCode2, GitCompareArrows, MessageSquarePlus, Square, SquareCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuTrigger } from '@/components/ui/context-menu';
type PrV2FileHeaderContextMenuState = {
  isViewed: boolean;
  isCollapsed: boolean;
  isFileLevelDraftOpen: boolean;
};
type PrV2FileHeaderContextMenuPermissions = {
  isReadOnly: boolean;
  canComment: boolean;
};
type PrV2FileHeaderContextMenuLinks = {
  oldGithubFileUrl: string | null;
  newGithubFileUrl: string | null;
};
type PrV2FileHeaderContextMenuView = {
  canToggleCodeView: boolean;
  isCodeView: boolean;
  codeViewToggleLabel: string;
};
type PrV2FileHeaderContextMenuActions = {
  onToggleViewed: () => void;
  onToggleCollapsed: () => void;
  onAddFileToAiChat: () => void;
  onToggleFileDraft: () => void;
  onToggleCodeView: () => void;
  onCopyFilePath: () => void;
  onCopyFileDiff: () => void;
  onOpenOldFileInGitHub: () => void;
  onOpenNewFileInGitHub: () => void;
};
type PrV2FileHeaderContextMenuProps = {
  filename: string;
  state: PrV2FileHeaderContextMenuState;
  permissions: PrV2FileHeaderContextMenuPermissions;
  links: PrV2FileHeaderContextMenuLinks;
  view: PrV2FileHeaderContextMenuView;
  actions: PrV2FileHeaderContextMenuActions;
  children: ReactNode;
};
export function PrV2FileHeaderContextMenu(t0) {
  const $ = _c(52);
  const {
    filename,
    state,
    permissions,
    links,
    view,
    actions,
    children
  } = t0;
  const showReviewActions = !permissions.isReadOnly || view.canToggleCodeView;
  let t1;
  if ($[0] !== children) {
    t1 = <ContextMenuTrigger asChild={true}>{children}</ContextMenuTrigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `Actions for ${filename}`;
  let t3;
  if ($[2] !== state.isViewed) {
    t3 = state.isViewed ? <Square className="mr-2 size-3.5 text-zinc-400" /> : <SquareCheck className="mr-2 size-3.5 text-zinc-400" />;
    $[2] = state.isViewed;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = state.isViewed ? "Mark as unviewed" : "Mark as viewed";
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ContextMenuShortcut>Space</ContextMenuShortcut>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== actions.onToggleViewed || $[6] !== t3 || $[7] !== t4) {
    t6 = <ContextMenuItem onSelect={actions.onToggleViewed}>{t3}{t4}{t5}</ContextMenuItem>;
    $[5] = actions.onToggleViewed;
    $[6] = t3;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== state.isCollapsed) {
    t7 = state.isCollapsed ? <ChevronsUpDown className="mr-2 size-3.5 text-zinc-400" /> : <ChevronsDownUp className="mr-2 size-3.5 text-zinc-400" />;
    $[9] = state.isCollapsed;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = state.isCollapsed ? "Expand file" : "Collapse file";
  let t9;
  if ($[11] !== actions.onToggleCollapsed || $[12] !== t7 || $[13] !== t8) {
    t9 = <ContextMenuItem onSelect={actions.onToggleCollapsed}>{t7}{t8}</ContextMenuItem>;
    $[11] = actions.onToggleCollapsed;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== actions.onAddFileToAiChat || $[16] !== actions.onToggleCodeView || $[17] !== actions.onToggleFileDraft || $[18] !== permissions.canComment || $[19] !== permissions.isReadOnly || $[20] !== showReviewActions || $[21] !== state.isFileLevelDraftOpen || $[22] !== view) {
    t10 = showReviewActions ? <><ContextMenuSeparator />{!permissions.isReadOnly ? <><ContextMenuItem onSelect={actions.onAddFileToAiChat}><Bot className="mr-2 size-3.5 text-zinc-400" />Add to AI chat</ContextMenuItem><ContextMenuItem onSelect={actions.onToggleFileDraft} disabled={!permissions.canComment}><MessageSquarePlus className="mr-2 size-3.5 text-zinc-400" />{state.isFileLevelDraftOpen ? "Close file comment" : "Add file comment"}{!state.isFileLevelDraftOpen ? <ContextMenuShortcut>C</ContextMenuShortcut> : null}</ContextMenuItem></> : null}{view.canToggleCodeView ? <ContextMenuItem onSelect={actions.onToggleCodeView}>{view.isCodeView ? <GitCompareArrows className="mr-2 size-3.5 text-zinc-400" /> : <FileCode2 className="mr-2 size-3.5 text-zinc-400" />}{view.isCodeView ? "View diff" : view.codeViewToggleLabel}</ContextMenuItem> : null}</> : null;
    $[15] = actions.onAddFileToAiChat;
    $[16] = actions.onToggleCodeView;
    $[17] = actions.onToggleFileDraft;
    $[18] = permissions.canComment;
    $[19] = permissions.isReadOnly;
    $[20] = showReviewActions;
    $[21] = state.isFileLevelDraftOpen;
    $[22] = view;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ContextMenuSeparator />;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Copy className="mr-2 size-3.5 text-zinc-400" />;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== actions.onCopyFilePath) {
    t13 = <ContextMenuItem onSelect={actions.onCopyFilePath}>{t12}Copy file path</ContextMenuItem>;
    $[26] = actions.onCopyFilePath;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Copy className="mr-2 size-3.5 text-zinc-400" />;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== actions.onCopyFileDiff) {
    t15 = <ContextMenuItem onSelect={actions.onCopyFileDiff}>{t14}Copy entire diff contents</ContextMenuItem>;
    $[29] = actions.onCopyFileDiff;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ContextMenuSeparator />;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = !links.oldGithubFileUrl;
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ExternalLink className="mr-2 size-3.5 text-zinc-400" />;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== actions.onOpenOldFileInGitHub || $[34] !== t17) {
    t19 = <ContextMenuItem onSelect={actions.onOpenOldFileInGitHub} disabled={t17}>{t18}View old file in GitHub</ContextMenuItem>;
    $[33] = actions.onOpenOldFileInGitHub;
    $[34] = t17;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const t20 = !links.newGithubFileUrl;
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ExternalLink className="mr-2 size-3.5 text-zinc-400" />;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== actions.onOpenNewFileInGitHub || $[38] !== t20) {
    t22 = <ContextMenuItem onSelect={actions.onOpenNewFileInGitHub} disabled={t20}>{t21}View new file in GitHub</ContextMenuItem>;
    $[37] = actions.onOpenNewFileInGitHub;
    $[38] = t20;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== t10 || $[41] !== t13 || $[42] !== t15 || $[43] !== t19 || $[44] !== t2 || $[45] !== t22 || $[46] !== t6 || $[47] !== t9) {
    t23 = <ContextMenuContent className="min-w-56" aria-label={t2}>{t6}{t9}{t10}{t11}{t13}{t15}{t16}{t19}{t22}</ContextMenuContent>;
    $[40] = t10;
    $[41] = t13;
    $[42] = t15;
    $[43] = t19;
    $[44] = t2;
    $[45] = t22;
    $[46] = t6;
    $[47] = t9;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== t1 || $[50] !== t23) {
    t24 = <ContextMenu>{t1}{t23}</ContextMenu>;
    $[49] = t1;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  return t24;
}